import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../Auth/types';
import { FormErrorsSignUp } from '../../Types/TypeErrors';
import { api, clearAuthHeader, setAuthHeader } from '../../utils/api';
import { isAxiosError } from 'axios';

export const signUp = createAsyncThunk(
  "auth/sign-up",
  async (credentials: FormErrorsSignUp, thunkAPI) => {
    try {
      const res = await api.post("/auth-user/sign-up", credentials);
      setAuthHeader(res.data.authUser.token);
      return { ...res.data.authUser };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        const data = error.response?.data;

        // Якщо бек повертає { nameUser: "..."}
        if (data?.nameUser) {
          return thunkAPI.rejectWithValue({ nameUser: data.nameUser });
        }

        // Якщо бек повертає { name: "..." }
        if (data?.name) {
          return thunkAPI.rejectWithValue({ name: data.name });
        }

        // Якщо бек повертає { errors: {...} }
        if (data?.errors && typeof data.errors === "object") {
          return thunkAPI.rejectWithValue(data.errors);
        }

        // Якщо повернули message
        if (typeof data?.message === "string") {
          return thunkAPI.rejectWithValue({ general: data.message });
        }

        return thunkAPI.rejectWithValue({ general: error.message });
      }

      return thunkAPI.rejectWithValue({ general: "Unknown error" });
    }
  }
);


export interface CredentialsSignIn {
  email: string;
  password: string;
}

export const logIn = createAsyncThunk(
  'auth/sign-in',
  async (credentials: CredentialsSignIn, thunkAPI) => {
    try {
      const res = await api.post('/auth-user/sign-in', credentials);
      setAuthHeader(res.data.authUser.token);
      return {
        id: res.data.authUser.id,
        name: res.data.authUser.name,
        email: res.data.authUser.email,
        avatarUser: res.data.authUser.avatar,
        token: res.data.authUser.token,
      };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        // сервер може повернути firewall помилку в message
        const data = error.response?.data;
        const serverErrors = error.response?.data?.errors || error.response?.data?.message;
        if (serverErrors) {
          // Якщо message — рядок, повертаємо у полі general
          if (typeof serverErrors === 'string') {
            return thunkAPI.rejectWithValue({ general: serverErrors });
          }
          if (data?.nameUser) {
          return thunkAPI.rejectWithValue({ nameUser: data.nameUser });
        }

        // Якщо бек повертає { name: "..." }
        if (data?.name) {
          return thunkAPI.rejectWithValue({ name: data.name });
        }
          // Якщо об’єкт, повертаємо як є
          if (typeof serverErrors === 'object') {
            return thunkAPI.rejectWithValue(serverErrors);
          }
        }
        return thunkAPI.rejectWithValue({ general: error.message });
      }
      return thunkAPI.rejectWithValue({ general: "Unknown error" });
    }
  }
);

export const logOut = createAsyncThunk(
  'auth/sign-out',
  async (id: string, thunkAPI) => { // id передається сюди
    try {
      const state = thunkAPI.getState() as RootState;
      const persistedToken = state.auth.token;

      if (!persistedToken) {
        return thunkAPI.rejectWithValue('Token missing');
      }

      await api.post(
        `/auth-user/sign-out/${id}`,
        {}, // тіло POST, якщо потрібно
        {
          headers: {
            Authorization: `Bearer ${persistedToken}`,
          },
        }
      );

      clearAuthHeader();
    } catch (error) {
      let message = "Something went wrong in log out";

      if (isAxiosError(error)) {
        message = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        message = error.message;
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateUserAccount = createAsyncThunk(
  'auth/updateUserAccount',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const persistedToken = state.auth.token;

    if (!persistedToken) return thunkAPI.rejectWithValue('Time is out, please login in 😉');

    setAuthHeader(persistedToken);

    try {
      const res = await api.get("/auth-users-task/main-info/get-current-account");
      return {
        user: res.data.getCurrectUser,
        nameUser: res.data.getCurrectUser.nameUser,
        token: persistedToken
      }; 
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Something went wrong while fetching user';
      return thunkAPI.rejectWithValue(message);
    }
  }
);


interface UpdateNameResponse {
  name: string;
}

interface UpdateNameArgs {
  name: string;
}

// Тип для помилок з бекенду
interface ValidationErrors {
  name?: string;
  [key: string]: string | undefined;
}

export const updateUserAccountName = createAsyncThunk<
  UpdateNameResponse,
  UpdateNameArgs,
  { state: RootState; rejectValue: string | ValidationErrors }
>(
  "auth/updateUserAccountName",
  async ({ name }, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.token;

      if (!token) return thunkAPI.rejectWithValue('Token or user ID missing');

      const res = await api.post(
        "/auth-users-task/main-info/update-main-info-name",
        {name},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return {
        ...res.data
      };

    } catch (error: unknown) {
      if (isAxiosError(error)) {
        const serverErrors = error.response?.data?.errors;
        if (serverErrors && typeof serverErrors === 'object') {
            return thunkAPI.rejectWithValue(serverErrors);
        }
        return thunkAPI.rejectWithValue({ general: error.message });
      }
        return thunkAPI.rejectWithValue({ general: "Unknown error" });
      }
    }
);

export const updateUserAccountEmail = createAsyncThunk(
  'auth/updateUserAccountEmail',
  async ({ email }: { email: string }, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const token = state.auth.token;

      if (!token) return thunkAPI.rejectWithValue('Unable to update user: token missing');

      const res = await api.post("/auth-users-task/main-info/update-main-info-email",
        {email},
        {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

        return {
          ...res.data
        };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        const serverErrors = error.response?.data?.errors;
        if (serverErrors && typeof serverErrors === 'object') {
            return thunkAPI.rejectWithValue(serverErrors);
        }
        return thunkAPI.rejectWithValue({ general: error.message });
      }
        return thunkAPI.rejectWithValue({ general: "Unknown error" });
      }
    }
);

export const updateUserAvatar = createAsyncThunk<
  { avatarUser: string },
  { avatarUser: File },
  {
    rejectValue: {
      errorIdUser?: string;
      errorUploadAvatar?: string;
      avatarUser?: string
    }
  }
>(
  'auth/updateUserAccountAvatar',
  async ({ avatarUser }, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const token = state.auth.token;

    if (!token) {
      return thunkAPI.rejectWithValue({ avatarUser: 'Unable to update user: token missing' });
    }

    try {
      const formData = new FormData();
      formData.append("avatar", avatarUser);

      const res = await api.post(
        `/auth-users-task/main-info/get-current-account/update-avatar`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return { avatarUser: res.data.avatarUser }; // бекенд повертає avatarUser
    } catch (error) {
      if (isAxiosError(error)) {
        const data = error.response?.data;
        if (data && 'errorIdUser' in data) {
          return thunkAPI.rejectWithValue({ errorIdUser: data.errorIdUser });
        }
        if (data && 'errorUploadAvatar' in data) {
          return thunkAPI.rejectWithValue({ errorUploadAvatar: data.errorUploadAvatar });
        }
        return thunkAPI.rejectWithValue({ avatarUser: data?.message || error.message });
      }
      return thunkAPI.rejectWithValue({ avatarUser: 'Unknown error' });
    }
  }
);

export const UpdateUserPassword = createAsyncThunk(
  "auth/UpdatePasswordUser",
  async (
    { id, password, confirmPassword }: { id: string; password: string; confirmPassword: string },
    thunkAPI
  ) => {

    const state = thunkAPI.getState() as RootState;
    const token = state.auth.token;

    if (!token) {
      return thunkAPI.rejectWithValue({ general: "Token missing" });
    }

    try {
      const res = await api.post(
        `/auth-users-task/main-info-update-password/${id}`,
        { password, confirmPassword },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return res.data;

    } catch (error) {
        if (isAxiosError(error)) {
          const data = error.response?.data;

          // ✅ Якщо backend повертає samePassword
          if (data?.samePassword) {
            console.log(data?.samePassword)
            return thunkAPI.rejectWithValue({ samePassword: data.samePassword });
          }

          // ✅ Якщо backend повертає message
          if (data?.message) {
            return thunkAPI.rejectWithValue({ general: data.message });
          }

          // ✅ Якщо бекенд повертає Joi errors
          if (data?.errors) {
            return thunkAPI.rejectWithValue(data.errors);
          }

          return thunkAPI.rejectWithValue({ general: error.message });
        }

        return thunkAPI.rejectWithValue({ general: "Unknown error" });
      }

  }
);



export const DeleteAccountUser = createAsyncThunk(
  'auth/DeleteAccountUser',
  async ({ id }: { id: string }, thunkAPI) => {

    const state = thunkAPI.getState() as RootState;
    const token = state.auth.token;
    if (!token) {
      return thunkAPI.rejectWithValue('Unable to delete user: token missing');
    }

    try {
      const res = await api.delete(`/auth-users-task/main-info/delete-account/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log(res.data)
      clearAuthHeader();
      localStorage.clear()

      console.log(res.data)
      return res.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const serverErrors = error.response?.data?.errors;
        console.log(serverErrors)
        if (serverErrors && typeof serverErrors === 'object') {
            return thunkAPI.rejectWithValue(serverErrors);
        }
        return thunkAPI.rejectWithValue({ general: error.message });
      }
        return thunkAPI.rejectWithValue({ general: "Unknown error" });
      }
  }
)
